import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Check, Printer, RotateCcw, CheckCircle2, CloudOff, Cloud, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import api from '../../api';

// ── Struttura checklist Brand Package — Eden Agents PB 0.7 (Standard v1.1) ──────
const CHECKLIST = [
  { title: 'Logo — variante colore principale (V1)', items: [
    { key: 'logo_v1_horizontal_white_bg',  file: 'logo_[cliente]_v1_horizontal_white_bg.png' },
    { key: 'logo_v1_horizontal_transparent', file: 'logo_[cliente]_v1_horizontal_transparent.png' },
    { key: 'logo_v1_vertical_white_bg',    file: 'logo_[cliente]_v1_vertical_white_bg.png' },
    { key: 'logo_v1_vertical_transparent', file: 'logo_[cliente]_v1_vertical_transparent.png' },
    { key: 'logo_v1_mono_black',           file: 'logo_[cliente]_v1_mono_black.png' },
    { key: 'logo_v1_mono_white',           file: 'logo_[cliente]_v1_mono_white.png' },
    { key: 'logo_v1_icon',                 file: 'logo_[cliente]_v1_icon.png' },
    { key: 'logo_v1_master_svg',           file: 'logo_[cliente]_v1_master.svg' },
    { key: 'logo_v1_on_primary_color',     file: 'logo_[cliente]_v1_on_primary_color.png' },
    { key: 'logo_v1_on_secondary_color',   file: 'logo_[cliente]_v1_on_secondary_color.png', optional: true },
  ]},
  { title: 'Logo — variante colore secondaria (V2)', note: 'se esiste', items: [
    { key: 'logo_v2_horizontal_white_bg',    file: 'logo_[cliente]_v2_horizontal_white_bg.png', optional: true },
    { key: 'logo_v2_horizontal_transparent', file: 'logo_[cliente]_v2_horizontal_transparent.png', optional: true },
    { key: 'logo_v2_mono_black',             file: 'logo_[cliente]_v2_mono_black.png', optional: true },
    { key: 'logo_v2_mono_white',             file: 'logo_[cliente]_v2_mono_white.png', optional: true },
    { key: 'logo_v2_icon',                   file: 'logo_[cliente]_v2_icon.png', optional: true },
    { key: 'logo_v2_master_svg',             file: 'logo_[cliente]_v2_master.svg', optional: true },
  ]},
  { title: 'Logo — watermark', items: [
    { key: 'logo_watermark_light', file: 'logo_[cliente]_watermark_light.png' },
    { key: 'logo_watermark_dark',  file: 'logo_[cliente]_watermark_dark.png' },
  ]},
  { title: 'Brand identity', items: [
    { key: 'brand_colors_md',      file: 'brand_colors.md' },
    { key: 'brand_colors_pdf',     file: 'brand_colors.pdf', optional: true },
    { key: 'brand_typography_md',  file: 'brand_typography.md' },
    { key: 'brand_guidelines_pdf', file: 'brand_guidelines.pdf', optional: true },
    { key: 'moodboard',            file: 'moodboard.jpg / .pdf', optional: true },
  ]},
  { title: 'Fonts', items: [
    { key: 'font_primario',   file: '[font_primario].ttf / .otf / .woff' },
    { key: 'font_secondario', file: '[font_secondario].ttf / .otf / .woff', optional: true },
  ]},
  { title: 'Pattern & texture', items: [
    { key: 'pattern_primary',   file: 'pattern_[cliente]_primary.png' },
    { key: 'pattern_secondary', file: 'pattern_[cliente]_secondary.png', optional: true },
    { key: 'texture',           file: 'texture_[cliente].jpg', optional: true },
    { key: 'background_light',  file: 'background_[cliente]_light.jpg' },
    { key: 'background_dark',   file: 'background_[cliente]_dark.jpg' },
  ]},
  { title: 'Elementi grafici', items: [
    { key: 'icons_pack',      file: 'icons_pack_[cliente].svg / .png' },
    { key: 'shapes_elements', file: 'shapes_elements_[cliente].svg' },
    { key: 'overlays',        file: 'overlays_[cliente].png', optional: true },
  ]},
  { title: 'Social templates', items: [
    { key: 'template_post_square',   file: 'template_post_square_1080x1080' },
    { key: 'template_story_reel',    file: 'template_story_reel_1080x1920' },
    { key: 'template_carousel_slide',file: 'template_carousel_slide' },
    { key: 'cover_facebook',         file: 'cover_facebook_[cliente].jpg' },
    { key: 'cover_linkedin',         file: 'cover_linkedin_[cliente].jpg' },
    { key: 'cover_youtube',          file: 'cover_youtube_[cliente].jpg' },
    { key: 'profile_picture',        file: 'profile_picture_[cliente].png' },
  ]},
  { title: 'Video branding', items: [
    { key: 'intro',             file: 'intro_[cliente].mp4 / .mov' },
    { key: 'outro',             file: 'outro_[cliente].mp4 / .mov' },
    { key: 'lower_third',       file: 'lower_third_[cliente].mov' },
    { key: 'watermark_animated',file: 'watermark_animated_[cliente].mov' },
    { key: 'color_lut',         file: 'color_lut_[cliente].cube', optional: true },
  ]},
  { title: 'Brand voice', items: [
    { key: 'brand_voice_guidelines', file: 'brand_voice_guidelines.md' },
    { key: 'buyer_persona',          file: 'buyer_persona_[cliente].md' },
    { key: 'competitor_reference',   file: 'competitor_reference.md' },
  ]},
];

const ALL_ITEMS = CHECKLIST.flatMap(s => s.items);
const TOTAL = ALL_ITEMS.length;

function slugify(name) {
  return (name || 'cliente')
    .trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'cliente';
}

export default function BrandChecklistSection({ clienteId, clienteNome }) {
  const [state, setState] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [openSections, setOpenSections] = useState({}); // default: tutte collassate
  const saveTimer = useRef(null);

  const toggleSection = (title) => setOpenSections(p => ({ ...p, [title]: !p[title] }));
  const allOpen = CHECKLIST.every(s => openSections[s.title]);
  const setAll = (open) => setOpenSections(open ? Object.fromEntries(CHECKLIST.map(s => [s.title, true])) : {});

  const slug = useMemo(() => slugify(clienteNome), [clienteNome]);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    api.get(`/cliente_brand_checklist/${clienteId}`)
      .then(r => { if (alive) { setState(r.data?.state || {}); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [clienteId]);

  const persist = (nextState) => {
    setSaveStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/cliente_brand_checklist/${clienteId}`, { state: nextState })
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, 500);
  };

  const toggle = (key) => {
    const next = { ...state };
    if (next[key]) delete next[key];
    else next[key] = true;
    setState(next);
    persist(next);
  };

  const resetAll = () => {
    if (!window.confirm('Azzerare tutta la checklist per questo cliente?')) return;
    setState({});
    persist({});
  };

  const checked = ALL_ITEMS.filter(it => state[it.key]).length;
  const pct = Math.round((checked / TOTAL) * 100);
  const fileLabel = (f) => f.replace(/\[cliente\]/g, slug);

  const SaveIndicator = () => {
    if (saveStatus === 'saving') return <span style={{ fontSize:11, color:'var(--text-dim)', display:'inline-flex', alignItems:'center', gap:5 }}><Cloud size={12}/> Salvataggio…</span>;
    if (saveStatus === 'saved')  return <span style={{ fontSize:11, color:'var(--green)', display:'inline-flex', alignItems:'center', gap:5 }}><CheckCircle2 size={12}/> Salvato</span>;
    if (saveStatus === 'error')  return <span style={{ fontSize:11, color:'var(--red)', display:'inline-flex', alignItems:'center', gap:5 }}><CloudOff size={12}/> Errore</span>;
    return null;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:14 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>Brand package checklist</div>
          <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:2 }}>Eden Agents · Playbook 0.7 · Standard v1.1</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <SaveIndicator/>
          <span style={{ fontSize:11.5, fontWeight:600, color:'var(--cyan)', background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:7, padding:'4px 10px' }}>
            {checked} / {TOTAL}
          </span>
          <button onClick={() => setAll(!allOpen)}
            style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, padding:'6px 11px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-muted)', cursor:'pointer', touchAction:'manipulation' }}>
            {allOpen ? <><ChevronsDownUp size={12}/> Comprimi</> : <><ChevronsUpDown size={12}/> Espandi</>}
          </button>
          <button onClick={resetAll}
            style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, padding:'6px 11px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-muted)', cursor:'pointer', touchAction:'manipulation' }}>
            <RotateCcw size={12}/> Reset
          </button>
          <button onClick={() => window.print()}
            style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, padding:'6px 11px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-muted)', cursor:'pointer', touchAction:'manipulation' }}>
            <Printer size={12}/> Stampa
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11.5, color:'var(--text-muted)', marginBottom:6 }}>
          <span>Completamento</span>
          <span style={{ color: pct === 100 ? 'var(--green)' : 'var(--cyan)', fontWeight:600 }}>{pct}%</span>
        </div>
        <div style={{ height:6, background:'var(--bg3)', borderRadius:999, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${pct}%`, background: pct === 100 ? 'var(--green)' : 'var(--cyan)', borderRadius:999, transition:'width 0.3s ease' }}/>
        </div>
      </div>

      {/* Sezioni */}
      {!loaded ? (
        <div style={{ padding:'32px 0', textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Caricamento…</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {CHECKLIST.map(sec => {
            const secChecked = sec.items.filter(it => state[it.key]).length;
            const isOpen = !!openSections[sec.title];
            const secComplete = secChecked === sec.items.length;
            return (
              <div key={sec.title} style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', background:'var(--surface)' }}>
                <div onClick={() => toggleSection(sec.title)}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 14px', cursor:'pointer', touchAction:'manipulation', transition:'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  {isOpen
                    ? <ChevronDown size={14} color="var(--text-dim)" style={{ flexShrink:0 }}/>
                    : <ChevronRight size={14} color="var(--text-dim)" style={{ flexShrink:0 }}/>}
                  <span style={{ fontSize:11.5, fontWeight:600, color:'var(--text)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    {sec.title}
                  </span>
                  {sec.note && <span style={{ fontSize:10, color:'var(--text-dim)' }}>— {sec.note}</span>}
                  <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10.5, fontWeight:600, marginLeft:'auto', flexShrink:0,
                    color: secComplete ? 'var(--green)' : 'var(--text-muted)',
                    background: secComplete ? 'var(--green-dim)' : 'var(--bg3)', borderRadius:999, padding:'2px 9px' }}>
                    {secComplete && <Check size={10}/>}{secChecked}/{sec.items.length}
                  </span>
                </div>
                {isOpen && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:4, padding:'4px 10px 12px', borderTop:'1px solid var(--border)' }}>
                  {sec.items.map(it => {
                    const on = !!state[it.key];
                    return (
                      <button key={it.key} onClick={() => toggle(it.key)}
                        style={{ display:'flex', alignItems:'flex-start', gap:9, padding:'7px 9px', borderRadius:8,
                          border:'1px solid transparent', background:'transparent', cursor:'pointer', textAlign:'left',
                          width:'100%', touchAction:'manipulation', transition:'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <span style={{ width:17, height:17, minWidth:17, minHeight:17, boxSizing:'border-box', marginTop:1,
                          borderRadius:5, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                          border: on ? 'none' : '1.5px solid var(--border-h)',
                          background: on ? 'var(--cyan)' : 'transparent', transition:'all 0.15s' }}>
                          {on && <Check size={11} color="var(--on-accent)" strokeWidth={3}/>}
                        </span>
                        <span style={{ fontSize:12, fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight:1.45,
                          color: on ? 'var(--text)' : it.optional ? 'var(--text-dim)' : 'var(--text-muted)',
                          wordBreak:'break-word' }}>
                          {fileLabel(it.file)}
                          {it.optional && <span style={{ color:'var(--text-dim)', fontFamily:'inherit' }}> ·opz</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop:18, paddingTop:12, borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', fontSize:10.5, color:'var(--text-dim)' }}>
        <span>Eden Agents · Playbook 0.7 · v1.1</span>
        <span>Le voci "·opz" sono facoltative</span>
      </div>
    </div>
  );
}
